import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import Alert from './Alert';
import { getProductImageUrl as getConfigProductImageUrl } from '../config';
import { Editor } from '@tinymce/tinymce-react';

const UpdateProductModal = ({ isOpen, onClose, onProductUpdated, product }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    tags: '',
    seo_title: '',
    meta_description: '',
    base_price: '',
    sale_price: '',
    sku: '',
    quantity: 0
  });
  
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]); // Track original images when modal opens
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Alert state management
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  // Variants state
  const [variants, setVariants] = useState([]);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantValue, setNewVariantValue] = useState('');
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);



  const API_BASE_URL = 'https://petifyshop.mycartly.in';

  // Alert helper functions
  const showAlert = (type, title, message) => {
    setAlert({
      show: true,
      type,
      title,
      message
    });
  };

  const hideAlert = () => {
    setAlert({
      show: false,
      type: 'info',
      title: '',
      message: ''
    });
  };

  // Populate form when product prop changes
  useEffect(() => {
    if (isOpen && product) {
      populateFormData(product);
    }
  }, [isOpen, product]);

  // Populate form with product data
  const populateFormData = (productData) => {
    if (!productData || typeof productData !== 'object') {
      setError('Invalid product data structure');
      return;
    }
    
    // Populate form with complete product data
    setFormData({
      name: productData.name || '',
      description: productData.description || '',
      status: productData.status || 'active',
      tags: productData.tags || '',
      seo_title: productData.seo_title || '',
      meta_description: productData.meta_description || '',
      base_price: productData.base_price || '',
      sale_price: productData.sale_price || '',
      sku: productData.sku || '',
      quantity: productData.quantity || 0,
      primary_image_version: productData.primary_image_version || 1
    });

    // Load existing variants
    if (productData.variants) {
      try {
        const parsedVariants = typeof productData.variants === 'string' 
          ? JSON.parse(productData.variants) 
          : productData.variants;
        setVariants(Array.isArray(parsedVariants) ? parsedVariants : []);
      } catch (e) {
        setVariants([]);
      }
    } else {
      setVariants([]);
    }

    // Load existing images with the correct primary version
    loadExistingImages(productData, productData.primary_image_version || 1);
  };

  // We'll use the product data passed as props directly
  // No need to fetch from API since we already have the data

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        status: 'active',
        tags: '',
        seo_title: '',
        meta_description: '',
        base_price: '',
        sale_price: '',
        sku: '',
        quantity: 0,
        primary_image_version: 1
      });
      setExistingImages([]);
      setOriginalImages([]);
      setVariants([]);
      setNewVariantName('');
      setNewVariantValue('');
      setSelectedVariantIndex(null);
      setImages([]);
      setError('');
      setSuccess('');
      

    }
  }, [isOpen]);

  const loadExistingImages = async (product, primaryVersionOverride = null) => {
    // Ensure product is an object with product_id
    if (!product || typeof product !== 'object' || !product.product_id) {
      console.error('Invalid product data for loading images:', product);
      setExistingImages([]);
      return;
    }

    const imageUrls = [];
    
    // Get the primary image version (priority: override parameter > formData > product data > default to 1)
    const primaryVersion = primaryVersionOverride || formData.primary_image_version || product?.primary_image_version || 1;
    
    // Check for up to 6 images (v1 to v6) by testing if they exist
    for (let i = 1; i <= 6; i++) {
      const baseUrl = getConfigProductImageUrl(product);
      // Properly generate URL for each version by replacing any version number with the current version
      const imageUrl = baseUrl ? baseUrl.replace(/_v\d+\.png/, `_v${i}.png`) : null;
      
      try {
        // Test if image exists by making a HEAD request
        const response = await fetch(imageUrl, { 
          method: 'HEAD',
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          // Add timestamp to prevent caching issues
          const timestamp = new Date().getTime();
          const urlWithTimestamp = `${imageUrl}?t=${timestamp}`;
          
          imageUrls.push({
            url: urlWithTimestamp,
            version: i,
            exists: true,
            isPrimary: i === primaryVersion
          });
        }
      } catch (error) {
        // Image doesn't exist, skip it
        // Image not found
      }
    }
    setExistingImages(imageUrls);
    setOriginalImages(imageUrls); // Store original images for comparison
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({
      ...prev,
      description: content
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (files.length > 6) {
      setError('Maximum 6 images allowed');
      return;
    }

    // Validate file size and type
    const validFiles = [];
    for (let file of files) {
      
      if (file.size > 400 * 1024) { // 400KB
        setError(`Image "${file.name}" is too large. Maximum size is 400KB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      
      validFiles.push(file);
    }

    setImages(validFiles);
    setError('');
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };






  // Remove existing image
  const removeExistingImage = async (index) => {
    const imageToDelete = existingImages[index];
    
    try {
      // Call the delete API to remove the file from server
      const response = await fetch(`${API_BASE_URL}/admin/products/${product.product_id}/delete-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          version: imageToDelete.version
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showAlert('success', 'Success', 'Image deleted successfully');
        
        // Remove from local state
        const newImages = existingImages.filter((_, i) => i !== index);
        setExistingImages(newImages);
        

      } else {
        console.error('Failed to delete image:', data.error);
        showAlert('error', 'Error', `Failed to delete image: ${data.error}`);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showAlert('error', 'Error', 'Failed to delete image. Please try again.');
    }
  };

  // Set image as primary
  const setAsPrimaryImage = async (version) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${product.product_id}/set-primary-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          version: version
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showAlert('success', 'Success', `Image v${version} set as primary successfully`);
        
        // Update the local product data to reflect the new primary image
        setFormData(prev => ({
          ...prev,
          primary_image_version: version
        }));
        
        // Also update the product prop to reflect the new primary image
        if (product) {
          product.primary_image_version = version;
        }
        
        // Update existing images state to reflect new primary status
        setExistingImages(prevImages => 
          prevImages.map(img => ({
            ...img,
            isPrimary: img.version === version
          }))
        );
        
        // Reload images to refresh URLs and primary status with new timestamp
        await loadExistingImages(product, version);
        
        // Notify parent component that product was updated
        if (onProductUpdated) {
          onProductUpdated();
        }
      } else {
        console.error('Failed to set primary image:', data.error);
        showAlert('error', 'Error', `Failed to set primary image: ${data.error}`);
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
      showAlert('error', 'Error', 'Failed to set primary image. Please try again.');
    }
  };

  // Variant management functions
  const addVariantName = () => {
    if (!newVariantName.trim()) {
      setError('Variant name is required');
      return;
    }

    // Check if variant name already exists
    if (variants.some(v => v.name.toLowerCase() === newVariantName.trim().toLowerCase())) {
      setError('Variant name already exists');
      return;
    }

    const newVariant = {
      name: newVariantName.trim(),
      values: []
    };

    setVariants(prev => [...prev, newVariant]);
    setNewVariantName('');
    setError('');
  };

  const addVariantValue = () => {
    if (selectedVariantIndex === null || selectedVariantIndex === '' || !newVariantValue.trim()) {
      setError('Please select a variant and enter a value');
      return;
    }

    const variant = variants[selectedVariantIndex];
    if (!variant) {
      setError('Selected variant not found');
      return;
    }

    if (variant.values.includes(newVariantValue.trim())) {
      setError('This value already exists for this variant');
      return;
    }

    setVariants(prev => prev.map((variant, index) => {
      if (index === selectedVariantIndex) {
        return {
          ...variant,
          values: [...variant.values, newVariantValue.trim()]
        };
      }
      return variant;
    }));

    setNewVariantValue('');
    setError('');
  };

  const removeVariant = (variantIndex) => {
    setVariants(prev => prev.filter((_, index) => index !== variantIndex));
    if (selectedVariantIndex === variantIndex) {
      setSelectedVariantIndex(null);
    }
  };

  const removeVariantValue = (variantIndex, valueIndex) => {
    setVariants(prev => prev.map((variant, index) => {
      if (index === variantIndex) {
        return {
          ...variant,
          values: variant.values.filter((_, vIndex) => vIndex !== valueIndex)
        };
      }
      return variant;
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Product name is required');
      return false;
    }
    
    if (!formData.base_price || formData.base_price <= 0) {
      setError('Valid base price is required');
      return false;
    }
    
    if (formData.sale_price && formData.sale_price >= formData.base_price) {
      setError('Sale price must be less than base price');
      return false;
    }
    
    if (formData.sale_price && formData.sale_price <= 0) {
      setError('Sale price must be greater than 0');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token missing. Please login again.');
        setLoading(false);
        return;
      }

      // Prepare FormData
      const submitData = new FormData();
      
      // Add product ID for update
      submitData.append('product_id', product.product_id);
      
      // Add form fields
      submitData.append('name', formData.name);
      submitData.append('description', formData.description || '');
      submitData.append('base_price', formData.base_price);
      submitData.append('sale_price', formData.sale_price || '');
      submitData.append('quantity', formData.quantity || '0');
      submitData.append('status', formData.status);
      submitData.append('tags', formData.tags || '');
      submitData.append('seo_title', formData.seo_title || '');
      submitData.append('meta_description', formData.meta_description || '');
      submitData.append('sku', formData.sku || '');

      // Add new images if any
      if (images.length > 0) {
        images.forEach((image, index) => {
          submitData.append('images', image);
        });
      }

      // Add variants
      if (variants.length > 0) {
        submitData.append('variants', JSON.stringify(variants));
      } else {
        submitData.append('variants', JSON.stringify([]));
      }

      
      const prepareImageOrderData = () => {
        const orderData = [];
        
        // Only include reshuffle data if there are existing images that need reordering
        // For now, we'll let the individual delete and reshuffle APIs handle the reordering
        // This function will be used for future enhancements if needed
        
        return orderData;
      };

      // Get deleted image versions
      const getDeletedImageVersions = () => {
        // Track which images were deleted by comparing original vs current
        const originalVersions = originalImages.map(img => img.version);
        const currentVersions = existingImages.map(img => img.version);
        const deletedVersions = originalVersions.filter(version => !currentVersions.includes(version));
        return deletedVersions;
      };

      // Add image order and deletion data
      const imageOrderData = prepareImageOrderData();
      const deletedImagesData = getDeletedImageVersions();
      
      if (imageOrderData.length > 0) {
        submitData.append('image_order', JSON.stringify(imageOrderData));
      }
      
      if (deletedImagesData.length > 0) {
        submitData.append('deleted_images', JSON.stringify(deletedImagesData));
      }

      const response = await fetch(`${API_BASE_URL}/updateproduct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData,
      });

      // Try to parse response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const textResponse = await response.text();
        setError('Server returned invalid response');
        setLoading(false);
        return;
      }

      if (response.ok) {
        if (data.success) {
          showAlert('success', 'Success', 'Product updated successfully!');
          
          // Notify parent
          if (onProductUpdated) {
            onProductUpdated();
          }
          
          // Clear form state
          setTimeout(() => {
            setImages([]);
            setError('');
            setSuccess('');
            onClose();
          }, 1500);
          
        } else {
          showAlert('error', 'Error', data.message || data.error || 'Product update failed');
        }
      } else {
        showAlert('error', 'Error', data.error || data.message || `Server error: ${response.status}`);
      }

    } catch (networkError) {
      showAlert('error', 'Error', `Network error: ${networkError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !fetchingData) {
      setImages([]);
      setError('');
      setSuccess('');
      setVariants([]);
      setNewVariantName('');
      setNewVariantValue('');
      setSelectedVariantIndex(null);
      setFetchingData(false);
      onClose();
    }
  };

  if (!isOpen || !product) return null;

  // Show loading state while fetching product data
  if (fetchingData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Update Product</h2>
          <button
            onClick={handleClose}
                                      disabled={loading || fetchingData}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter product name"
                disabled={loading || fetchingData}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Editor
                apiKey="7615mhf2af6t6vajjc8x96l2xmbxkdvic8d9zns9cqs2rb2y"
                value={formData.description}
                onEditorChange={handleEditorChange}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Original Price (MRP) *
              </label>
              <input
                type="number"
                name="base_price"
                value={formData.base_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading || fetchingData}
                required
              />
              <p className="text-xs text-gray-500 mt-1">This will show as strikethrough price if sale price is set</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sale Price (Current Price)
              </label>
              <input
                type="number"
                name="sale_price"
                value={formData.sale_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading || fetchingData}
              />
              <p className="text-xs text-gray-500 mt-1">Actual selling price (must be less than original price)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading || fetchingData}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SKU
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Product SKU"
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="tag1, tag2, tag3"
                disabled={loading || fetchingData}
              />
            </div>
          </div>

          {/* SEO Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title
                </label>
                <input
                  type="text"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SEO optimized title"
                  disabled={loading || fetchingData}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Meta description for search engines"
                  disabled={loading || fetchingData}
                />
              </div>
            </div>
          </div>

          {/* Variants Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Variants</h3>
            
            {/* Add Variant Name */}
            <div className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  placeholder="Enter variant name (e.g., Color, Size)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading || fetchingData}
                />
                <button
                  type="button"
                  onClick={addVariantName}
                  disabled={loading || fetchingData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  Add Variant
                </button>
              </div>

              {/* Add Variant Values */}
              {variants.length > 0 && (
                <div className="flex space-x-2">
                  <select
                    value={selectedVariantIndex !== null ? selectedVariantIndex : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedVariantIndex(value === '' ? null : parseInt(value));
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading || fetchingData}
                  >
                    <option value="">Select variant to add values</option>
                    {variants.map((variant, index) => (
                      <option key={index} value={index}>{variant.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newVariantValue}
                    onChange={(e) => setNewVariantValue(e.target.value)}
                    placeholder="Enter value (e.g., Red, Large)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading || fetchingData}
                  />
                  <button
                    type="button"
                    onClick={addVariantValue}
                    disabled={loading || fetchingData || selectedVariantIndex === null}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Add Value
                  </button>
                </div>
              )}

              {/* Display Variants */}
              {variants.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Current Variants:</h4>
                  {variants.map((variant, variantIndex) => (
                    <div key={variantIndex} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{variant.name}</h5>
                        <button
                          type="button"
                          onClick={() => removeVariant(variantIndex)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          disabled={loading || fetchingData}
                        >
                          Remove Variant
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {variant.values.map((value, valueIndex) => (
                          <span
                            key={valueIndex}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                          >
                            {value}
                            <button
                              type="button"
                              onClick={() => removeVariantValue(variantIndex, valueIndex)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              disabled={loading || fetchingData}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {variant.values.length === 0 && (
                          <span className="text-sm text-gray-500 italic">No values added yet</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Upload */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>
            
            {/* Existing Images */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Current Images ({existingImages.length})
                </label>

              </div>
                                <p className="text-xs text-gray-500 mb-2">
                    Tip: Click "Set Primary" to choose which image displays in product cards • Click the × button to delete images
                  </p>
              {existingImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {existingImages.map((imageData, index) => (
                    <div 
                      key={index} 
                      className="relative group"
                    >
                      <img
                        src={imageData.url}
                        alt={`Current ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-24 bg-gray-100 rounded-lg hidden items-center justify-center border-2 border-gray-200">
                        <ImageIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      

                      
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={loading}
                      >
                        ×
                      </button>
                      
                      {/* Set as Primary button */}
                      <button
                        type="button"
                        onClick={() => setAsPrimaryImage(imageData.version)}
                        className={`absolute bottom-1 right-1 px-2 py-1 text-xs rounded transition-opacity ${
                          imageData.isPrimary 
                            ? 'bg-green-600 text-white opacity-100' 
                            : 'bg-blue-500 text-white opacity-0 group-hover:opacity-100 hover:bg-blue-600'
                        }`}
                        disabled={loading}
                        title={imageData.isPrimary ? 'This is the primary image' : 'Set as primary image'}
                      >
                        {imageData.isPrimary ? '✓ Primary' : 'Set Primary'}
                      </button>
                      
                      <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                        v{imageData.version}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No existing images found</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload New Images (Max 6, 400KB each)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload-update"
                    disabled={loading || fetchingData}
                  />
                  <label
                    htmlFor="image-upload-update"
                    className="cursor-pointer flex flex-col items-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click to upload new images (will replace existing)
                    </span>
                    <span className="text-xs text-gray-500">
                      PNG, JPG, JPEG, GIF, WEBP up to 400KB each
                    </span>
                  </label>
                </div>
              </div>

              {/* New Image Preview */}
              {images.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Images ({images.length}/6)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Tip: Click the × button to delete images
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <div 
                        key={index} 
                        className="relative group"
                      >
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={loading}
                        >
                          ×
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t pt-6 flex space-x-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || fetchingData}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fetchingData}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Update Product'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Alert Component */}
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={hideAlert}
        autoClose={alert.type === 'success' || alert.type === 'error'}
        autoCloseDelay={3000}
      />
    </div>
  );
};

export default UpdateProductModal;